import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";
import { identityDeepLink, SeenByChips } from "./IdentityInventoryPanel";
import PostureWeaknesses from "./PostureWeaknesses";
import { moreFindingsLabel } from "./moreFindingsLabel";
import DrawerShell, { DrawerFailed, DrawerLoading, InlineDetailShell } from "./DrawerShell";

/**
 * GRC → Assets → Devices (Phase 5): the device inventory read from Defender for Endpoint
 * (machines API) and the Advanced Hunting DeviceInfo snapshot, reconciled onto one row per
 * device. Inventory only — no row here is a control, a risk or a score input. The drawer shows
 * the identities that logged on (from DeviceLogonEvents) and the findings that name the device.
 */
export const DEVICE_SOURCE_LABELS = {
  MdeMachines: "Defender for Endpoint",
  DefenderXdrDeviceInfo: "Defender XDR DeviceInfo",
  DefenderXdrDeviceLogons: "Logon events",
};
const deviceSourceLabel = (s) => DEVICE_SOURCE_LABELS[s] || s;

export const deviceDeepLink = (deviceId) =>
  `/SecurityInsights?tab=assets&type=device&deviceId=${encodeURIComponent(deviceId)}`;

export const deviceDisplayName = (d) => d.displayName || d.dnsName || d.mdeDeviceId || d.deviceKey;

const RISK_TONES = {
  High: "bg-red-50 text-red-700 border-red-200",
  Medium: "bg-orange-50 text-orange-700 border-orange-200",
  Low: "bg-yellow-50 text-yellow-700 border-yellow-200",
  Informational: "bg-gray-50 text-gray-600 border-gray-200",
  None: "bg-green-50 text-green-700 border-green-200",
};

/** Axaris finding severities, worst first, with the tone of the findings chip at that level. */
const FINDING_SEVERITIES = [
  ["critical", "bg-red-100 text-red-800 border-red-300"],
  ["high", "bg-red-50 text-red-700 border-red-200"],
  ["medium", "bg-orange-50 text-orange-700 border-orange-200"],
  ["low", "bg-yellow-50 text-yellow-700 border-yellow-200"],
];

/**
 * The findings total first, labelled, then its worst severity in a chip of its own ("60 total"
 * [4 critical]), so it is never read against the Defender alert risk column as the same measure —
 * and never as one number: "55 · 55 low" read as 55.55. The full breakdown is on hover.
 */
function FindingsChip({ count, severity }) {
  if (!count) return <span className="text-gray-300">0</span>;
  const worst = severity ? FINDING_SEVERITIES.find(([key]) => (severity[key] || 0) > 0) : null;
  const breakdown = severity
    ? FINDING_SEVERITIES.map(([key]) => `${severity[key] || 0} ${key}`).join(", ")
    : null;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap"
      title={breakdown ? `${count} open Axaris finding(s): ${breakdown}` : undefined}>
      <span><span className="font-semibold text-gray-900">{count}</span> <span className="text-gray-500">total</span></span>
      {worst && (
        <span className={`px-1.5 py-0.5 rounded border font-semibold ${worst[1]}`}>
          {severity[worst[0]]} {worst[0]}
        </span>
      )}
    </span>
  );
}

const PAGE_SIZE = 50;
const SORTS = [
  { key: "displayName", label: "Device" },
  { key: "dnsName",     label: "DNS name" },
  { key: "osPlatform",  label: "OS" },
  { key: "health",      label: "Health" },
  // Microsoft's machine risk score, which follows ACTIVE alerts only — an inactive device with
  // dozens of open findings reads "None" here. Named for what it is, so it is not read as Axaris's.
  { key: "risk",        label: "Defender alert risk",
    title: "Microsoft Defender for Endpoint's own risk score for this device, based on its active alerts only. Axaris findings are counted separately." },
  { key: "sourceSeen",  label: "Last seen by source" },
];

const Stat = ({ label, value, tone = "text-gray-900" }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
  </div>
);

export default function DeviceInventoryPanel({ organizationId, initialDeviceId = null }) {
  const apiCall = useApi();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [health, setHealth] = useState("");
  const [risk, setRisk] = useState("");
  const [dc, setDc] = useState("");             // "" | "true"
  const [seen, setSeen] = useState("");         // "" | "false"
  const [hasFindings, setHasFindings] = useState("");
  const [seenBy, setSeenBy] = useState("");
  const [sort, setSort] = useState("displayName");
  const [desc, setDesc] = useState(false);
  const [selected, setSelected] = useState(null);
  // Same fix as the identity panel: open the panel on the click, not on the response, or a click
  // looks like nothing happened and people click the row repeatedly.
  const [opening, setOpening] = useState(null);
  const [openFailed, setOpenFailed] = useState(false);
  const openRequest = useRef(0);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (health) p.set("healthStatus", health);
    if (risk) p.set("riskScore", risk);
    if (dc) p.set("domainController", dc);
    if (seen) p.set("seenInLastSync", seen);
    if (hasFindings) p.set("hasFindings", hasFindings);
    if (seenBy) p.set("seenBy", seenBy);
    p.set("sort", sort);
    if (desc) p.set("desc", "true");
    p.set("page", String(page));
    p.set("pageSize", String(PAGE_SIZE));
    return p.toString();
  }, [search, health, risk, dc, seen, hasFindings, seenBy, sort, desc, page]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await apiCall("GET", `/defender-identity/devices?${query}`)); }
    catch (err) { setError(err?.response?.data?.message || "Could not load the device inventory."); }
    finally { setLoading(false); }
  }, [apiCall, query]);

  useEffect(() => { load(); }, [load, organizationId]);

  const openDetail = useCallback(async (id) => {
    const request = ++openRequest.current;
    setSelected(null);
    setOpenFailed(false);
    setOpening(id);
    try {
      const detail = await apiCall("GET", `/defender-identity/devices/${id}`);
      if (openRequest.current !== request) return;   // a later click already won
      setSelected(detail);
      setOpening(null);
    } catch {
      if (openRequest.current !== request) return;
      setOpenFailed(true);
    }
  }, [apiCall]);

  const closeDetail = useCallback(() => {
    openRequest.current++; setSelected(null); setOpening(null); setOpenFailed(false);
  }, []);

  useEffect(() => {
    if (initialDeviceId) openDetail(initialDeviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDeviceId]);

  const resetPage = (setter) => (v) => { setter(v); setPage(1); };
  const toggleSort = (key) => { if (sort === key) setDesc(d => !d); else { setSort(key); setDesc(false); } setPage(1); };

  const items = data?.items || [];
  const total = data?.total || 0;
  const counts = data?.counts;
  const openFindings = data?.openFindings || {};
  const openFindingSeverity = data?.openFindingSeverity || {};
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="device-inventory">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Device inventory</h3>
          <div className="text-xs text-gray-500">Devices from Defender for Endpoint (machines) and Defender XDR DeviceInfo, one row per device. Inventory only; no risks are created from these rows.</div>
        </div>
      </div>

      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label="Devices" value={counts.total} />
          <Stat label="Defender high risk" value={counts.highRisk} tone="text-red-700" />
          <Stat label="Defender medium risk" value={counts.mediumRisk} tone="text-orange-700" />
          <Stat label="Inactive" value={counts.inactive} tone="text-gray-500" />
          <Stat label="Domain controllers" value={counts.domainControllers} tone="text-rose-700" />
          <Stat label="Not in last sync" value={counts.notSeenInLastSync} tone={counts.notSeenInLastSync > 0 ? "text-amber-700" : "text-gray-900"} />
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap items-center gap-2" aria-busy={loading}>
        <form onSubmit={e => { e.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }} className="flex items-center gap-2">
          <input value={searchDraft} onChange={e => setSearchDraft(e.target.value)} placeholder="Search name, DNS, IP…" aria-label="Search devices"
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 w-56 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
          <button type="submit" className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200">Search</button>
        </form>
        <select value={seenBy} onChange={e => resetPage(setSeenBy)(e.target.value)} aria-label="Seen by" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">Seen by any source</option>
          {(data?.sources || []).map(s => <option key={s} value={s}>Seen by {deviceSourceLabel(s)}</option>)}
        </select>
        <select value={risk} onChange={e => resetPage(setRisk)(e.target.value)} aria-label="Risk score" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">Any Defender risk</option>
          {["High", "Medium", "Low", "Informational", "None"].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={health} onChange={e => resetPage(setHealth)(e.target.value)} aria-label="Health status" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">Any health</option>
          {["Active", "Inactive", "ImpairedCommunication", "NoSensorData"].map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input type="checkbox" checked={dc === "true"} onChange={e => resetPage(setDc)(e.target.checked ? "true" : "")} className="rounded accent-indigo-600" />
          Domain controllers
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input type="checkbox" checked={seen === "false"} onChange={e => resetPage(setSeen)(e.target.checked ? "false" : "")} className="rounded accent-indigo-600" />
          Not in last sync
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input type="checkbox" checked={hasFindings === "true"} onChange={e => resetPage(setHasFindings)(e.target.checked ? "true" : "")} className="rounded accent-indigo-600" />
          Has findings
        </label>
        <div className="ml-auto text-xs text-gray-500 tabular-nums flex items-center gap-1.5" role="status" aria-live="polite" data-testid="device-count">
          {loading && <span className="inline-block w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" aria-hidden="true" />}
          {loading ? "Loading devices…" : `${total.toLocaleString()} device${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto relative">
        {loading && <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden" data-testid="device-loading"><div className="h-full w-1/3 bg-indigo-500" style={{ animation: "identity-loader 1s linear infinite" }} /></div>}
        <table className={`min-w-full text-xs transition-opacity ${loading ? "opacity-50 pointer-events-none" : ""}`}>
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              {SORTS.map(s => (
                <th key={s.key} scope="col" className="text-left font-semibold px-3 py-2 whitespace-nowrap" title={s.title}>
                  <button type="button" onClick={() => toggleSort(s.key)} className="hover:text-gray-900 flex items-center gap-1">
                    {s.label}{sort === s.key && <span aria-hidden="true">{desc ? "↓" : "↑"}</span>}
                  </button>
                </th>
              ))}
              <th scope="col" className="text-left font-semibold px-3 py-2 whitespace-nowrap">Seen by</th>
              <th scope="col" className="text-left font-semibold px-3 py-2">Status</th>
              <th scope="col" className="text-left font-semibold px-3 py-2 whitespace-nowrap" title="Open Axaris findings that name this device, under your own risk visibility, with the most severe level shown. A separate measure from the Defender alert risk.">Axaris findings</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {!loading && items.length === 0 && (
              <tr><td colSpan={SORTS.length + 3} className="px-3 py-8 text-center text-gray-500">
                {total === 0 && !search && !health && !risk && !dc && !seen && !hasFindings && !seenBy
                  ? "No devices yet. Run a sync from Integration Settings → Defender for Identity, or wait for the nightly sync."
                  : "No devices match these filters."}
              </td></tr>
            )}
            {items.map(d => (
              <tr key={d.id} onClick={() => openDetail(d.id)} className="hover:bg-indigo-50/40 cursor-pointer">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{deviceDisplayName(d)}</div>
                  {d.isDomainController && <span className="px-1 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">Domain controller</span>}
                </td>
                <td className="px-3 py-2 text-gray-700 font-mono max-w-[240px] truncate" title={d.dnsName || undefined}>{d.dnsName || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-2 text-gray-700">{d.osPlatform || <span className="text-gray-300">—</span>}{d.osVersion ? <span className="text-gray-400"> {d.osVersion}</span> : null}</td>
                <td className="px-3 py-2 text-gray-700">{d.healthStatus || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-2">
                  {d.riskScore
                    ? <span className={`px-1.5 py-0.5 rounded border ${RISK_TONES[d.riskScore] || "bg-gray-50 text-gray-600 border-gray-200"}`}>{d.riskScore}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{d.lastSeenBySourceAt ? formatUtcDateTime(d.lastSeenBySourceAt) : <span className="text-gray-300">not reported</span>}</td>
                <td className="px-3 py-2"><SeenByChips seenBy={d.seenBy} labels={DEVICE_SOURCE_LABELS} /></td>
                <td className="px-3 py-2">
                  {!d.seenInLastSync
                    ? <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Not in last sync</span>
                    : <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">Reported</span>}
                </td>
                <td className="px-3 py-2 tabular-nums">
                  <FindingsChip count={openFindings[d.id] || 0} severity={openFindingSeverity[d.id]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pageCount > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-xs text-gray-600">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Previous</button>
            <span>Page {page} of {pageCount}</span>
            <button type="button" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {selected && <DeviceDetailDrawer detail={selected} onClose={closeDetail} />}
      {!selected && (opening !== null || openFailed) && (
        <DrawerShell onClose={closeDetail} label="Device details"
          title={openFailed ? "Could not open this device" : "Opening…"}>
          {openFailed
            ? <DrawerFailed onRetry={() => openDetail(opening)} testId="device-detail-failed"
                message="The device details could not be loaded." />
            : <DrawerLoading testId="device-detail-loading" message="Loading device details…" />}
        </DrawerShell>
      )}
    </div>
  );
}

// A titled section of the device details. Inline (Security Operations) it is a closed section opened on
// click, like "Raw source row"; in the drawer it is a heading with its content, as before.
const DetailSection = ({ inline, title, testId, children }) => (inline ? (
  <details className="mt-4" data-testid={testId}>
    <summary className="text-[11px] uppercase tracking-wide text-gray-400 cursor-pointer">{title}</summary>
    <div className="mt-1">{children}</div>
  </details>
) : (
  <>
    <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">{title}</div>
    {children}
  </>
));

// `inline` lays it out in the page (InlineDetailShell) rather than as a drawer. `onOpenRisk(riskId)` and
// `onOpenIdentity(identityId)`, when given, keep a finding or an identity on the calling page instead of
// linking to the Risk Register / Security Insights (Security Operations uses both). `findingsLimit`
// lists only that many findings, the rest behind "+ N more" (null lists them all).
export function DeviceDetailDrawer({ detail, onClose, inline = false, onOpenRisk = null, onOpenIdentity = null, findingsLimit = null }) {
  const Shell = inline ? InlineDetailShell : DrawerShell;
  const apiCall = useApi();
  const d = detail.device;
  const [findings, setFindings] = useState(null);
  const [showAllFindings, setShowAllFindings] = useState(false);
  const shownFindings = findings && findingsLimit != null && !showAllFindings ? findings.slice(0, findingsLimit) : findings;

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/defender-identity/devices/${d.id}/findings`)
      .then(rows => { if (!cancelled) setFindings(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setFindings([]); });
    return () => { cancelled = true; };
  }, [apiCall, d.id]);

  const Row = ({ label, children }) => (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-gray-100 text-xs">
      <div className="text-gray-500">{label}</div>
      <div className="col-span-2 text-gray-800 break-all">{children ?? <span className="text-gray-300">—</span>}</div>
    </div>
  );

  const loggedOnIdentities = (
    <>
      {(!detail.identities || detail.identities.length === 0) && <div className="text-xs text-gray-400 py-1.5">No logon events observed for this device.</div>}
      {detail.identities && detail.identities.length > 0 && (
        <ul className="space-y-1" data-testid="device-identities">
          {detail.identities.map((i, k) => (
            <li key={k} className="text-xs flex items-center justify-between gap-2 py-1 border-b border-gray-100">
              <span className="truncate">
                {i.identityAssetId != null
                  ? (onOpenIdentity
                    ? <button type="button" onClick={() => onOpenIdentity(i.identityAssetId)} className="font-medium text-gray-900 hover:underline text-left">{i.displayName || i.accountUpn || i.account}</button>
                    : <a href={identityDeepLink(i.identityAssetId)} className="font-medium text-gray-900 hover:underline">{i.displayName || i.accountUpn || i.account}</a>)
                  : <span className="text-gray-700">{i.account || "unknown account"} <span className="text-gray-400">· not in the identity inventory</span></span>}
                {i.isPrivileged && <span className="ml-1 px-1 rounded bg-rose-50 text-rose-700 border border-rose-200">Privileged</span>}
              </span>
              <span className="text-gray-500 whitespace-nowrap">{i.logons} logon{i.logons === 1 ? "" : "s"}{i.lastLogonAt ? ` · ${formatUtcDateTime(i.lastLogonAt)}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  return (
    <Shell title={deviceDisplayName(d)} subtitle={`${d.dnsName ?? ""}${d.osPlatform ? ` · ${d.osPlatform}` : ""}`}
      onClose={onClose} label="Device details" testId="device-detail">
        <div className="-mt-2 mb-2 text-xs"><SeenByChips seenBy={d.seenBy} labels={DEVICE_SOURCE_LABELS} /></div>

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">Associated findings</div>
        {findings === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}
        {findings && findings.length === 0 && <div className="text-xs text-gray-400 py-1.5">No findings name this device.</div>}
        {findings && findings.length > 0 && (
          <ul className="space-y-1.5 py-1" data-testid="device-findings">
            {shownFindings.map(f => (
              <li key={f.linkId} className={`text-xs rounded border px-2.5 py-2 ${f.isActive ? (f.isOpen ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-200") : "border-dashed border-gray-200 text-gray-400"}`}>
                <div className="flex items-center justify-between gap-2">
                  {onOpenRisk
                    ? <button type="button" onClick={() => onOpenRisk(f.unifiedRiskId)} className="font-medium text-gray-900 hover:underline truncate text-left">{f.title || f.findingKey}</button>
                    : <a href={`/risks?riskId=${encodeURIComponent(f.unifiedRiskId)}`} className="font-medium text-gray-900 hover:underline truncate">{f.title || f.findingKey}</a>}
                  <span className={`px-1 rounded border ${f.isOpen ? "bg-red-100 text-red-700 border-red-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{f.isOpen ? "Open" : f.status}</span>
                </div>
                <div className="text-gray-500 mt-0.5">{f.riskSource} · matched by {f.matchKind} · last seen {formatUtcDateTime(f.lastSeenAt)}{!f.isActive && " · no longer named by this finding"}</div>
              </li>
            ))}
          </ul>
        )}

        {findingsLimit != null && findings && findings.length > findingsLimit && (
          <button type="button" onClick={() => setShowAllFindings(x => !x)} aria-expanded={showAllFindings}
            data-testid="device-findings-more"
            className="mt-1 text-xs font-medium text-indigo-700 hover:underline">
            {showAllFindings ? "Show fewer" : moreFindingsLabel(findings, shownFindings)}
          </button>
        )}

        <DetailSection inline={inline} testId="device-identities-section"
          title={`Logged-on identities (30 days)${inline && detail.identities && detail.identities.length > 0 ? ` · ${detail.identities.length}` : ""}`}>
          {loggedOnIdentities}
        </DetailSection>

        {inline ? (
          <DetailSection inline title="Posture weaknesses" testId="device-posture-section">
            <PostureWeaknesses entity="devices" id={d.id} label="device" hideHeading />
          </DetailSection>
        ) : (
          <PostureWeaknesses entity="devices" id={d.id} label="device" />
        )}

        <DetailSection inline={inline} title="Device" testId="device-properties-section">
          <Row label="DNS name">{d.dnsName}</Row>
          <Row label="OS">{d.osPlatform}{d.osVersion ? ` ${d.osVersion}` : ""}</Row>
          <Row label="Type">{d.deviceType}{d.isDomainController ? " · Domain controller" : ""}</Row>
          <Row label="Health">{d.healthStatus}</Row>
          <Row label="Onboarding">{d.onboardingStatus}</Row>
          <Row label="Defender alert risk">{d.riskScore}</Row>
          <Row label="Exposure level">{d.exposureLevel}</Row>
          <Row label="Last IP">{d.lastIpAddress}</Row>
          <Row label="Machine group">{d.machineGroup}</Row>
        </DetailSection>

        <DetailSection inline={inline} title="Microsoft identifiers" testId="device-identifiers-section">
          <Row label="MDE device id">{d.mdeDeviceId}</Row>
          <Row label="Entra device id">{d.aadDeviceId}</Row>
          <Row label="Axaris key">{d.deviceKey}</Row>
        </DetailSection>

        <DetailSection inline={inline} title="Timeline" testId="device-timeline-section">
          <Row label="First seen by source">{d.firstSeenAtSource ? formatUtcDateTime(d.firstSeenAtSource) : null}</Row>
          <Row label="Last seen by source">{d.lastSeenBySourceAt ? formatUtcDateTime(d.lastSeenBySourceAt) : null}</Row>
          <Row label="First seen by Axaris">{formatUtcDateTime(d.firstSeenAt)}</Row>
          <Row label="Last seen by Axaris">{formatUtcDateTime(d.lastSeenAt)}</Row>
          <Row label="In last sync">{d.seenInLastSync ? "Yes" : "No — not reported by the last complete sync"}</Row>
        </DetailSection>

        {detail.sources && detail.sources.length > 0 && (
          <DetailSection inline={inline} title={`Seen by${inline ? ` · ${detail.sources.length}` : ""}`} testId="device-sources-section">
            <ul className="space-y-1.5" data-testid="device-source-records">
              {detail.sources.map(r => (
                <li key={`${r.source}-${r.sourceId}`} className="text-xs rounded border border-gray-200 px-2.5 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-800">{deviceSourceLabel(r.source)}</span>
                    <span className={r.seenInLastSync ? "text-green-700" : "text-amber-700"}>{r.seenInLastSync ? "in last sync" : "not in last sync"}</span>
                  </div>
                  <div className="text-gray-500 mt-0.5 font-mono break-all">{r.sourceId}</div>
                  <div className="text-gray-400">first {formatUtcDateTime(r.firstSeenAt)} · last {formatUtcDateTime(r.lastSeenAt)}</div>
                </li>
              ))}
            </ul>
          </DetailSection>
        )}

        {detail.rawJson && (
          <details className="mt-4">
            <summary className="text-xs text-gray-500 cursor-pointer">Raw source row</summary>
            <pre className="mt-2 text-[10px] bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
              {(() => { try { return JSON.stringify(JSON.parse(detail.rawJson), null, 2); } catch { return detail.rawJson; } })()}
            </pre>
          </details>
        )}
    </Shell>
  );
}
